import { useCallback, useEffect, useRef } from "react";
import { suggestedTools } from "plg/utils/configSchema";
import type { PlgSignupConfig, PlgStep, SignupAnswers } from "plg/types";
import { trackPlg } from "plg/analytics/events";
import { stepSlug } from "plg/analytics/slugs";
import { companyTraits } from "plg/analytics/identity";

/**
 * One sign-up step: onboarding_step_completed (+ tools_selected for a tools step) when it is continued
 * or skipped, onboarding_abandoned when the page is closed on it. Returns the "completed" callback.
 */
export default function useStepTracking(step: PlgStep, config: PlgSignupConfig, industry?: string) {
  const startedAt = useRef(Date.now());
  const completed = useRef(false);

  useEffect(() => {
    const onPageHide = () => {
      if (!completed.current) trackPlg("onboarding_abandoned", { last_step: stepSlug(step.key) });
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [step.key]);

  return useCallback(
    (values: SignupAnswers, skipped: boolean) => {
      completed.current = true;
      trackPlg("onboarding_step_completed", {
        step: stepSlug(step.key),
        skipped,
        time_on_step_ms: Date.now() - startedAt.current,
        ...(skipped ? {} : companyTraits(values)),
      });
      if (skipped) return;
      const suggested = suggestedTools(config, industry);
      step.fields
        .filter((f) => f.type === "toolPicker")
        .forEach((f) => {
          const tools = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : [];
          if (!tools.length) return;
          // conditional_hit: at least one pick came from the tools suggested for their industry.
          trackPlg("tools_selected", { tools, count: tools.length, conditional_hit: tools.some((t) => suggested.includes(t)) });
        });
    },
    [step, config, industry]
  );
}
